import React from "react";
import { Alert, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NavigateFunction } from "react-router-dom";
import { useSyncSiteFlow } from "./useSyncSiteFlow";
import {
  dismissSyncBannerForCurrentSite,
  useCurrentSiteBannerDismissed,
} from "../utils/syncStore";
import { useSyncBannersHiddenForCurrentSite } from "../utils/syncBannerPreference";
import { HideSyncBannerCheckbox } from "./HideSyncBannerCheckbox";

/**
 * Unsynced-changes banner for the camera-level pages (Edit Camera, Edit
 * Ignore Zones, Edit Line Crossings, Edit Metadata Tag Zones).
 *
 * Counterpart of the About-page "Changes saved successfully! Please Sync Site"
 * warning Alert — same severity, voice and placement, driven by the shared
 * sync store, so it shows on every camera-level page whenever the site has
 * unsynced changes and disappears the moment a sync completes anywhere.
 *
 * Rendered in normal flow at the very top of the page, above the page's
 * absolute scroll container, exactly like the About-page alerts (PROD-35).
 * The container has no `top`, so it sits at its static position and the
 * whole page (nav included) shifts down under the banner instead of the
 * banner floating over the header and following the scroll.
 *
 * Two ways to make it go away, mirroring About:
 *  - X closes it until the next save on this site (dismissal lives in the
 *    sync store, so it survives moving between cameras on the same site).
 *  - "Don't show again for this site" hides it for the rest of the browser
 *    session (SUP-924).
 * The header Sync Site button is unaffected by either and still turns red
 * while changes are pending.
 */

export const SyncNeededBanner = ({
  navigate,
}: {
  navigate: NavigateFunction;
}) => {
  const { syncNeeded, syncing, syncApplies, syncNow, rebootDialog } =
    useSyncSiteFlow(navigate);
  const bannersHidden = useSyncBannersHiddenForCurrentSite();
  const dismissed = useCurrentSiteBannerDismissed();

  if (
    !syncApplies ||
    bannersHidden ||
    (dismissed && !syncing) ||
    (!syncNeeded && !syncing)
  ) {
    return <>{rebootDialog}</>;
  }

  return (
    <>
      {rebootDialog}
      <Alert
        severity="warning"
        sx={{ alignItems: "center", fontFamily: "Mulish" }}
        action={
          <>
            <HideSyncBannerCheckbox />
            <Button
              color="inherit"
              size="small"
              disabled={syncing}
              onClick={syncNow}
              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {syncing ? "Syncing..." : "Sync Site Now"}
            </Button>
            <IconButton
              aria-label="Close"
              color="inherit"
              size="small"
              disabled={syncing}
              onClick={() => dismissSyncBannerForCurrentSite()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </>
        }
      >
        This site has unsynced changes — settings won&apos;t take effect until
        you sync.
      </Alert>
    </>
  );
};
